import { useState, useEffect, useRef, useCallback } from "react";

// ── Joystick ──────────────────────────────────────────────────────────────────
function Joystick({ onDirection }) {
  const containerRef  = useRef(null);
  const dirRef        = useRef({ dx: 0, dy: 0 });
  const touchIdRef    = useRef(null);
  const baseRef       = useRef({ x: 0, y: 0 });
  const [thumbOffset, setThumbOffset] = useState({ x: 0, y: 0 });
  const [basePos,     setBasePos]     = useState(null);
  const JOYSTICK_RADIUS = 70;
  const THUMB_SIZE      = 60;

  // Block page scroll while joystick is active
  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener("touchmove",  prevent, { passive: false });
    document.addEventListener("touchstart", prevent, { passive: false });
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width    = "100%";
    return () => {
      document.removeEventListener("touchmove",  prevent);
      document.removeEventListener("touchstart", prevent);
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width    = "";
    };
  }, []);

  // Send direction to server at 50 ms intervals
  useEffect(() => {
    const iv = setInterval(() => {
      const { dx, dy } = dirRef.current;
      if (onDirection) onDirection(dx, dy);
    }, 50);
    return () => clearInterval(iv);
  }, [onDirection]);

  const getPos = (touch) => {
    const rect = containerRef.current.getBoundingClientRect();
    return { tx: touch.clientX - rect.left, ty: touch.clientY - rect.top };
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (touchIdRef.current !== null) return;
    const t = e.changedTouches[0];
    touchIdRef.current = t.identifier;
    const { tx, ty } = getPos(t);
    baseRef.current = { x: tx, y: ty };
    setBasePos({ x: tx, y: ty });
    setThumbOffset({ x: 0, y: 0 });
    dirRef.current = { dx: 0, dy: 0 };
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const t = Array.from(e.changedTouches).find(t2 => t2.identifier === touchIdRef.current);
    if (!t) return;
    const { tx, ty } = getPos(t);
    const ox   = tx - baseRef.current.x;
    const oy   = ty - baseRef.current.y;
    const dist = Math.hypot(ox, oy);
    let nx = ox, ny = oy;
    if (dist > JOYSTICK_RADIUS) {
      nx = (ox / dist) * JOYSTICK_RADIUS;
      ny = (oy / dist) * JOYSTICK_RADIUS;
    }
    setThumbOffset({ x: nx, y: ny });
    dirRef.current = { dx: nx / JOYSTICK_RADIUS, dy: ny / JOYSTICK_RADIUS };
  };

  const handleTouchEnd = (e) => {
    const stillDown = Array.from(e.touches).some(t => t.identifier === touchIdRef.current);
    if (!stillDown) {
      touchIdRef.current = null;
      setThumbOffset({ x: 0, y: 0 });
      setBasePos(null);
      dirRef.current = { dx: 0, dy: 0 };
    }
  };

  const ARROW_OFFSETS = [
    { arrow: "↑", x: 0,                    y: -JOYSTICK_RADIUS * 1.3 },
    { arrow: "↓", x: 0,                    y:  JOYSTICK_RADIUS * 1.3 },
    { arrow: "←", x: -JOYSTICK_RADIUS * 1.3, y: 0 },
    { arrow: "→", x:  JOYSTICK_RADIUS * 1.3, y: 0 },
  ];

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        width: "100%", height: "100%", position: "relative",
        touchAction: "none", userSelect: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {/* Outer ring — snaps to touch position */}
      <div style={{
        position: "absolute",
        left:      basePos ? basePos.x : "50%",
        top:       basePos ? basePos.y : "50%",
        transform: "translate(-50%, -50%)",
        width:  JOYSTICK_RADIUS * 2,
        height: JOYSTICK_RADIUS * 2,
        borderRadius: "50%",
        background:  "rgba(155,93,229,0.15)",
        border:      "2.5px solid rgba(155,93,229,0.5)",
        boxShadow:   "0 0 24px rgba(155,93,229,0.2)",
        pointerEvents: "none",
      }} />

      {/* Direction arrows */}
      {ARROW_OFFSETS.map(({ arrow, x, y }) => (
        <div key={arrow} style={{
          position: "absolute",
          left: basePos ? basePos.x + x : `calc(50% + ${x}px)`,
          top:  basePos ? basePos.y + y : `calc(50% + ${y}px)`,
          transform: "translate(-50%, -50%)",
          color: "rgba(155,93,229,0.4)",
          fontSize:"var(--fs-lg)",
          pointerEvents: "none",
          userSelect: "none",
        }}>{arrow}</div>
      ))}

      {/* Thumb */}
      <div style={{
        position: "absolute",
        width:  THUMB_SIZE,
        height: THUMB_SIZE,
        left:  basePos ? basePos.x + thumbOffset.x : `calc(50% + ${thumbOffset.x}px)`,
        top:   basePos ? basePos.y + thumbOffset.y : `calc(50% + ${thumbOffset.y}px)`,
        transform: "translate(-50%, -50%)",
        borderRadius: "50%",
        background:  "linear-gradient(135deg, #b97eff, #7b2fd8)",
        border:      "3px solid #000",
        boxShadow:   "4px 4px 0 #000, 0 0 20px #9b5de588",
        transition:  thumbOffset.x === 0 && thumbOffset.y === 0 ? "left 0.15s, top 0.15s" : "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize:"var(--fs-xl)",
      }}>🕹️</div>
    </div>
  );
}

// ── BumperPlayer ──────────────────────────────────────────────────────────────
export default function BumperPlayer({ socket, roomId, health, timeLeft, alive, eliminated, myColor, roundInfo, roundResult, myScore }) {
  const handleDirection = useCallback((dx, dy) => {
    if (socket && socket.connected) {
      socket.emit("bumper_input", { room_id: roomId, dx, dy });
    }
  }, [socket, roomId]);

  const healthColor = health > 50 ? "#3dffa0" : health > 25 ? "#ffe600" : "#ff3355";
  const timerColor  = timeLeft > 30 ? "#3dffa0" : timeLeft > 10 ? "#ffe600" : "#ff3355";

  // Between-rounds intermission
  if (roundResult && roundResult.round_num < roundResult.total_rounds) {
    const iWon = roundResult.round_winner === sessionStorage.getItem("playerName");
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-5 animate-slide-up">
        <div style={{ fontSize: "3.5rem" }}>{iWon ? "🏆" : "⏱"}</div>
        <h2 className="font-display text-white" style={{ fontSize:"var(--fs-2xl)" }}>
          Раунд {roundResult.round_num} завершён
        </h2>
        {roundResult.round_winner ? (
          <div className="card-cartoon text-center" style={{ padding: "0.8rem 1.5rem" }}>
            <p className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>Победитель</p>
            <p className="font-display" style={{ color: iWon ? "#ffe600" : "#fff", fontSize:"var(--fs-xl)" }}>
              {iWon ? "🏆 Ты!" : roundResult.round_winner}
            </p>
          </div>
        ) : (
          <p className="font-heading text-[#aaa]">Ничья!</p>
        )}
        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>
          Следующий раунд начнётся через несколько секунд...
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-3">

      {/* Status bar */}
      <div className="flex justify-between items-center gap-4">

        {/* Health bar */}
        <div className="flex-1">
          <div className="flex justify-between mb-1">
            <span className="font-heading text-[#aaa]" style={{ fontSize:"var(--fs-sm)" }}>❤️ Здоровье</span>
            <span className="font-display" style={{ color: healthColor, fontSize:"var(--fs-md)" }}>
              {Math.round(health)}%
            </span>
          </div>
          <div style={{
            height: "12px", background: "#1a1030",
            border: "2px solid #000", borderRadius: "6px", overflow: "hidden",
          }}>
            <div style={{
              width: `${Math.max(0, health)}%`, height: "100%",
              background: healthColor,
              boxShadow: `0 0 8px ${healthColor}`,
              transition: "width 0.2s, background 0.3s",
              borderRadius: "4px",
            }} />
          </div>
        </div>

        {/* Timer + score */}
        <div className="flex flex-col items-center gap-0.5">
          <div className="font-display text-center" style={{
            color: timerColor, fontSize:"var(--fs-2xl)",
            textShadow: `0 0 12px ${timerColor}`, minWidth: "3rem",
            animation: timeLeft <= 10 ? "wiggle .4s ease-in-out infinite" : "none",
          }}>
            {timeLeft}
          </div>
          <div className="font-heading text-center" style={{ color: "#ffe600", fontSize:"var(--fs-sm)", whiteSpace: "nowrap" }}>
            {myScore || 0} очк.
          </div>
        </div>
      </div>

      {/* Joystick area */}
      <div style={{
        flex: 1,
        background: "linear-gradient(135deg, #1a0a30 0%, #0d0820 100%)",
        border: "2.5px solid #000", borderRadius: "1.5rem",
        boxShadow: "6px 6px 0 #000", overflow: "hidden",
        minHeight: "280px",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative",
      }}>
        {/* Background glow */}
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${myColor}11 0%, transparent 70%)`,
        }} />

        {/* Round info badge */}
        {roundInfo && (
          <div className="font-heading" style={{
            position: "absolute", top: "0.6rem", left: "50%", transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.7)", border: "1.5px solid rgba(155,93,229,0.5)",
            borderRadius: "0.5rem", padding: "0.2rem 0.8rem",
            color: "rgba(255,255,255,0.7)", fontSize:"var(--fs-xs)", whiteSpace: "nowrap",
            pointerEvents: "none",
          }}>
            {roundInfo.emoji} Раунд {roundInfo.num}/{roundInfo.total} — {roundInfo.name}
          </div>
        )}

        <Joystick onDirection={handleDirection} />
      </div>

      {/* Hint */}
      <p className="font-heading text-center text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>
        Толкай соперников чтобы уничтожить их здоровье
      </p>
    </div>
  );
}
